import api from '@mint-lib/api';
import { useCrucialQuery } from '@mint-lib/query-client';
import { getRouteApi } from '@mint/routing';

import { queryClient } from '../api/queries/client.js';
import { QueryKey } from '../constants/queryKeys.js';
import {
  CampaignSchema,
  type CampaignType,
} from '../types/_campaigns/campaigns.js';
import { getNestedValue, type NestedKeyOf } from '../utils/helpers.js';

const getCampaignData = async (campaignId: string) => {
  const response = await api.get(
    `/api/datintell/v1/campaign_collections/${campaignId}/`,
  );

  return CampaignSchema.parse(response.data);
};

const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

export const useGetCampaignData = () => {
  const params = routeApi.useParams();

  const { data, isLoading, isError } = useCrucialQuery({
    queryKey: [QueryKey.CAMPAIGN_DATA, params.campaignId],
    queryFn: () => getCampaignData(params.campaignId),
    staleTime: Infinity,
  });

  return { data, isLoading, isError };
};

// use this function when you want to update the campaign data in the cache
export const setCampaignData = (
  campaignId: number,
  payload: Partial<CampaignType>,
) => {
  queryClient.setQueryData(
    [QueryKey.CAMPAIGN_DATA, String(campaignId)],
    (oldData: CampaignType) => ({
      ...oldData,
      ...payload,
    }),
  );
};

// use this hook when you want to get a specific key from the campaign data avoiding the re-renders if others keys are updated
export const useCampaignDataSelector = <K extends NestedKeyOf<CampaignType>>(
  path: K,
) => {
  const params = routeApi.useParams();

  const {
    data: specificKey,
    isLoading,
    isError,
  } = useCrucialQuery({
    queryKey: [QueryKey.CAMPAIGN_DATA, params.campaignId],
    queryFn: () => getCampaignData(params.campaignId),
    select: (data: CampaignType) => getNestedValue(data, path),
    staleTime: Infinity,
  });

  return { data: specificKey, isLoading, isError };
};
